/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/naming-convention */
import { Resource } from 'Generated/academy-graphql-types'
import {
  GoalType,
  Campaign,
  GoalCategory,
  GoalImpactCategory,
} from 'Generated/graphql-types'

// needed for backwards compatibility so we can display goals with old category values
export const categoriesIncludingOld: { [key in GoalCategory]: string } = {
  [GoalCategory.BoardManagement]: 'Board Management',
  [GoalCategory.Compliance]: 'Compliance',
  [GoalCategory.DataImpact]: 'Data & Impact',
  [GoalCategory.DeiAccessibility]: 'DEI & Accessibility',
  [GoalCategory.EventManagement]: 'Event Management',
  [GoalCategory.Finance]: 'Finance',
  [GoalCategory.Formation]: 'Formation',
  [GoalCategory.Fundraising]: 'Fundraising',
  [GoalCategory.Grants]: 'Grants',
  [GoalCategory.InternalCapacity]: 'Internal Capacity',
  [GoalCategory.MarketingCommunications]: 'Marketing Communications',
  [GoalCategory.MissionDriven]: 'Mission Driven',
  [GoalCategory.PeopleOperations]: 'People Operations',
  [GoalCategory.ProgramManagement]: 'Program Management',
  [GoalCategory.Storytelling]: 'Storytelling',
  [GoalCategory.StrategicPlanning]: 'Strategic Planning',
  [GoalCategory.TechnologySolutions]: 'Technology Solutions',
  [GoalCategory.VolunteerManagement]: 'Volunteer Management',
  [GoalCategory.Other]: 'Other',
  [GoalCategory.None]: 'Other',
}

export const categories: { [key in GoalCategory]?: string } = {
  [GoalCategory.BoardManagement]: 'Board Management',
  [GoalCategory.Compliance]: 'Compliance',
  [GoalCategory.DataImpact]: 'Data & Impact',
  [GoalCategory.DeiAccessibility]: 'DEI & Accessibility',
  [GoalCategory.EventManagement]: 'Event Management',
  [GoalCategory.Finance]: 'Finance',
  [GoalCategory.Formation]: 'Formation',
  [GoalCategory.Fundraising]: 'Fundraising',
  [GoalCategory.Grants]: 'Grants',
  [GoalCategory.MarketingCommunications]: 'Marketing Communications',
  [GoalCategory.PeopleOperations]: 'People Operations',
  [GoalCategory.ProgramManagement]: 'Program Management',
  [GoalCategory.Storytelling]: 'Storytelling',
  [GoalCategory.StrategicPlanning]: 'Strategic Planning',
  [GoalCategory.TechnologySolutions]: 'Technology Solutions',
  [GoalCategory.VolunteerManagement]: 'Volunteer Management',
  [GoalCategory.None]: 'Other',
  [GoalCategory.Other]: 'Other',
}

export const impactCategories: { [key in GoalImpactCategory]: string } = {
  [GoalImpactCategory.Mission]: 'Mission',
  [GoalImpactCategory.Organizational]: 'Organizational',
}

export const categoryOptions = Object.entries(categories).map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
)

export const categoryOptionsIncludingOld = Object.entries(
  categoriesIncludingOld
).map(([key, value]) => ({
  label: value,
  value: key,
}))

export enum GoalOwner {
  ALL_TEAM_MEMBERS,
}

export enum TaskOwner {
  NONE,
}

export const defaultOwners: { [key in GoalOwner]: string } = {
  [GoalOwner.ALL_TEAM_MEMBERS]: 'All Team Members',
}

export const defaultTaskOwners: { [key in TaskOwner]: string } = {
  [TaskOwner.NONE]: 'None',
}

export const defaultOwnersOptions = Object.entries(defaultOwners).map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
)

export const defaultTaskOwnersOptions = Object.entries(defaultTaskOwners).map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
)

export type User = {
  id?: number
  firstName: string
  lastName: string
}

export type Dimension = {
  id: number
  title: string
}

type GoalToDimension = {
  dimension: Dimension
}

export type Entry = {
  id: number
  value: number
  goalToDimension: GoalToDimension
}

export type EntryGroup = {
  id: number
  details: string | null
  reportedAt: string
  Entry: Entry[] | []
  archivedAt?: Date
}

export type ChecklistItem = {
  id: number
  isCompleted: boolean
  lastUpdatedUser: User | null
  owner: User | null
  title: string
  resourceId?: string | null
  updatedAt: string
  linkText: null
  linkUrl: null
}

export type ChecklistItemInput = {
  goalId: number
  title: string
  id?: number
  isCompleted?: boolean
  ownerId?: number | null
  resourceId?: string | null
  dueDate?: string | undefined
}

export type ChecklistItemWithResourceInfo = {
  title: string
  id?: number
  isCompleted?: boolean
  owner?: User | null
  resource?: Resource | null
  updatedAt: string
  dueDate?: string | null
  linkText: string | null
  linkUrl: string | null
  checklistItemGroupId?: number | null
  createdAt: string
  goalId?: number
}

export type CustomTaskLink = {
  linkText: string
  linkUrl: string
}

export type Goal = {
  id: number
  type: GoalType | null
  title: string | null
  category: GoalCategory | null
  ownerId: number | null
  owner: User | null
  metric: string | null
  target: number | null
  unitOfMeasurement: string | null
  createdAt?: Date
  archivedAt?: Date | null
  EntryGroup: EntryGroup[] | []
  GoalToDimension: GoalToDimension[]
  ChecklistItem: ChecklistItem[] | []
  lastUpdatedUser: User | null
  updatedAt?: Date
  templateLibraryId: number | null
}

export type EditedEntry = {
  id: number
  value?: number | null
  goalToDimension: GoalToDimension
}

export type FormattedCampaignGoalsData = Record<number, Campaign[]>

export interface IGoalTableProps {
  initiativeId: number
  initiativeTitle: string
  campaignGoals: FormattedCampaignGoalsData
}

export type DefaultGoalUpdateValues = {
  initiativeTitle: string
  goalTitle: string
  date: string
}

export const defaultGoalInProgress = {
  GoalToDimension: [],
  EntryGroup: [
    {
      id: 0,
      reportedAt: new Date().toISOString(),
      details: null,
      Entry: [],
    },
  ],
  id: 0,
  metric: 'Total ',
  title: 'Increase enrollment in afterschool reading program',
  unitOfMeasurement: 'Enrolled students',
  target: 150,
}

export const CAMPAIGN_GOAL_UNIT_OF_MEASUREMENT = 'Net Raised'
